import type { ReactNode } from "react";
import { images, type ImageAsset } from "@/config/images";
import { LuxuryVisionBackdrop } from "./LuxuryVisionBackdrop";

export function PageHero({
  eyebrow,
  title,
  body,
  image = images.hero,
  rotatingVision = true,
  rotationKey,
  breadcrumbs,
  children,
}: {
  eyebrow: string;
  title: string;
  body: string;
  image?: ImageAsset;
  rotatingVision?: boolean;
  rotationKey?: string;
  breadcrumbs?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="relative isolate overflow-hidden bg-primary pt-28 text-primary-foreground md:pt-36">
      {rotatingVision ? (
        <LuxuryVisionBackdrop rotationKey={rotationKey ?? title} priority />
      ) : (
        <img
          src={image.src}
          alt=""
          width={image.width}
          height={image.height}
          fetchPriority="high"
          className="absolute inset-0 -z-20 size-full object-cover"
        />
      )}
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(102deg,oklch(0.16_0.06_255/0.98)_0%,oklch(0.16_0.06_255/0.88)_55%,oklch(0.16_0.06_255/0.55)_100%)]" />
      <div className="container-page py-16 md:py-24">
        {breadcrumbs}
        <p className="eyebrow mt-8">{eyebrow}</p>
        <h1 className="display-1 mt-4 max-w-4xl text-balance">{title}</h1>
        <p className="mt-6 max-w-2xl text-lg leading-8 text-primary-foreground/75">{body}</p>
        {children ? <div className="mt-9">{children}</div> : null}
      </div>
    </section>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-primary-foreground/65">
      <ol className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="flex items-center gap-2">
            {index > 0 ? <span aria-hidden="true">/</span> : null}
            {item.href ? (
              <a href={item.href} className="transition-colors hover:text-accent">
                {item.label}
              </a>
            ) : (
              <span aria-current="page">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
